import type { QuotaAllowance, QuotaTierLimits } from '@/api/types'
import { gib, size, sizeMb, sizeUsage } from '@/ui/format'
import { FREE } from './words'

// 한 사람의 쿼터 한 축을 말하는 방법. VM 생성 페이지·변경 대화 상자와 사용자 상세가 같은 줄을 쓴다.

/**
 * 쓰는 양과 한도. **한도가 없는 축(`null`, 세지 않는다)은 쓰는 양만 말한다** — 서버가 거절하지 않는 숫자를
 * 한도처럼 보이면 화면과 거절이 다른 말을 한다. `0` 은 "하나도 안 된다" 라서 그대로 `n / 0` 이다.
 */
export function quotaText(used: number, limit: number | null, unit = ''): string {
  return limit === null ? `${used}${unit}` : `${used} / ${limit}${unit}`
}

/** 크기(GiB)의 쓰는 양과 한도. 한도가 단위를 정한다 — `100 / 256GB`·`0.5 / 2.0TB`. */
export function quotaSizeText(used: number, limit: number | null): string {
  return limit === null ? size(used) : sizeUsage(used, limit)
}

/** 메모리(MB)의 쓰는 양과 한도 — `quotaSizeText` 와 같은 줄. */
export function quotaMemoryText(usedMb: number, limitMb: number | null): string {
  return quotaSizeText(usedMb / 1024, gib(limitMb))
}

/**
 * 직위가 지는 네 한도를 한 줄로(F-72). **머리글 없이 혼자 서야 하므로 축의 이름을 값이 지고 간다** —
 * 좁은 화면에서는 표의 머리가 접히고, 직위를 고르는 목록에는 애초에 머리가 없다.
 * 비운 축은 `자유` 다: 세지 않는 축에 숫자를 붙이면 없는 한도가 한도처럼 보인다.
 */
export function limitsText(l: QuotaAllowance): string {
  const axis = (limit: number | null, show: (n: number) => string) => (limit === null ? FREE : show(limit))
  return [
    `VM ${axis(l.max_vms, (n) => `${n}대`)}`,
    `코어 ${axis(l.max_cores, String)}`,
    `메모리 ${axis(l.max_memory_mb, sizeMb)}`,
    `디스크 ${axis(l.max_disk_gb, size)}`,
  ].join(' · ')
}

/**
 * 직위의 한도를 축마다 — 직위 목록의 칸과 직위 상세의 사실 줄이 같은 순서·같은 글자를 쓴다.
 * 비운 축은 `자유` 다.
 */
export function limitAxes(l: QuotaAllowance): [string, string][] {
  const axis = (limit: number | null, show: (n: number) => string) => (limit === null ? FREE : show(limit))
  return [
    ['VM', axis(l.max_vms, (n) => `${n}대`)],
    ['CPU', axis(l.max_cores, (n) => `${n}코어`)],
    ['메모리', axis(l.max_memory_mb, sizeMb)],
    ['디스크', axis(l.max_disk_gb, size)],
  ]
}

/**
 * 사람이 든 직위를 지울 수 없다는 막힘과 할 일. 직위 화면은 목록의 인원으로 먼저 말하고, 그 사이 누가 들어와
 * 서버가 거절해도(`in_use` 의 `people`) 같은 문장이다 — 두 자리가 같은 막힘을 다른 말로 하지 않게.
 */
export function tierHeldSentence(people: number): string {
  return `이 직위에 ${people}명이 있어 지울 수 없습니다. 사용자 화면에서 다른 직위로 옮긴 뒤 지우세요.`
}

/**
 * 기본 직위를 지울 수 없다는 막힘과 할 일. 줄에는 지우기가 서지 않지만 창을 연 사이 그 직위가 기본이 되면
 * 창이 다시 읽은 줄로 이 문장을 말하고, 서버의 `is_default` 거절도 같은 문장이다.
 */
export const TIER_DEFAULT_BLOCKED = '기본 직위는 지울 수 없습니다. 다른 직위를 기본으로 지정한 뒤 지우세요.'

/** 그 직위를 지울 수 없는 이유. 사람이 들었거나 기본이면 막힘 문장, 아니면 `null`. */
export function tierBlocked(t: { people: number; is_default: boolean }): string | null {
  return t.people > 0 ? tierHeldSentence(t.people) : t.is_default ? TIER_DEFAULT_BLOCKED : null
}

/**
 * 직위를 만들고 고치는 대화 상자의 네 칸, 글자 그대로. 숫자가 아니라 문자열인 것이 요점이다 —
 * `''` 이 "자유" 라는 뜻을 진다.
 */
export type TierDraft = { vms: string; cores: string; memoryGb: string; diskGb: string }

export const EMPTY_TIER_DRAFT: TierDraft = { vms: '', cores: '', memoryGb: '', diskGb: '' }

/** 비운 칸은 본문에서 빠진다 — 생략이 곧 자유다. 메모리만 GB 로 적고 MB 로 간다. */
export function tierLimitsBody(d: TierDraft): QuotaTierLimits {
  return {
    ...(d.vms === '' ? {} : { max_vms: Number(d.vms) }),
    ...(d.cores === '' ? {} : { max_cores: Number(d.cores) }),
    ...(d.memoryGb === '' ? {} : { max_memory_mb: Number(d.memoryGb) * 1024 }),
    ...(d.diskGb === '' ? {} : { max_disk_gb: Number(d.diskGb) }),
  }
}

/**
 * 칸의 글자는 지금 한도다. 비운 칸은 자유라 보내는 본문에서 빠진다 — 그것이 다시 자유로 가는 유일한 길이다.
 *
 * **메모리는 올림으로 GB 가 된다.** 스펙이 1024 의 배수만 받으므로 지금 쓰는 값은 왕복해도 그대로지만,
 * 그 전에 들어간 1024 의 배수가 아닌 값은 내림이면 1GB 미만이 `0` 이 되고 저장을 누른 순간
 * 그 직위가 "메모리 한 칸도 안 된다" 로 바뀐다 — 고치려던 것의 정반대다. 올림은 있던 값을 잃지 않는다.
 */
export function tierDraftOf(l: QuotaAllowance): TierDraft {
  const text = (n: number | null) => (n === null ? '' : String(n))
  return {
    vms: text(l.max_vms),
    cores: text(l.max_cores),
    memoryGb: l.max_memory_mb === null ? '' : String(Math.ceil(l.max_memory_mb / 1024)),
    diskGb: text(l.max_disk_gb),
  }
}

/**
 * 카드의 메모리 하한이 내 메모리 쿼터에 남은 것보다 클 때의 알림(F-101). 막힘이 아니다 — 카드를 실은 신청은 어차피
 * 승인을 기다리고, 그 메모리는 승인하는 사람이 정한다. 그래서 VM 생성과 변경 신청은 이 경우 메모리 쿼터로 막지 않는다.
 */
export function floorPastQuotaSentence(floorMb: number): string {
  return `이 GPU 에는 메모리가 ${sizeMb(floorMb)} 이상 필요해 내 메모리 쿼터를 넘습니다. 관리자가 승인할 때 정합니다.`
}

/** 메모리 쿼터에 남은 MB. 한도가 없으면 `null` 이다 — 넘을 것이 없다. */
export function memoryLeftMb(q: { limits: { max_memory_mb: number | null }; used: { max_memory_mb: number } }): number | null {
  return q.limits.max_memory_mb === null ? null : q.limits.max_memory_mb - q.used.max_memory_mb
}
